/**
 * Frame-option resolution for the Add-a-frame dialog. Pure functions over the
 * pricing catalogue — unit-tested in frameOptions.test.ts.
 *
 * Any-paper upsell (owner 2026-07-26): a loose cart group is offered EVERY
 * stocked framed config — the frame range never shrinks to the group's paper
 * or size. The group's own paper is used where the config stocks it; else the
 * cheapest stocked paper stands in (see framedUpsellProducts). Picking an
 * option whose size or paper differs warns in the dialog; convertToFramed
 * moves the print to the frame's own size AND paper (the framed price
 * includes the print). Sorted smallest print size first.
 */

import {
  buildCatalog,
  parseSizeId,
  sizeLabel,
  type BorderStyle,
  type CartGroup,
  type FramedProduct,
  type PricingData,
  isMattedBorder,
} from '@rpl/pricing';
import { framedUpsellProducts } from '../../../lib/sizePaper';

/** Every framed product a loose group can be upgraded to (empty for framed groups). */
export function frameOptionsForGroup(
  data: PricingData,
  group: Pick<CartGroup, 'sizeId' | 'paper' | 'frameId'>,
  colour?: string,
): FramedProduct[] {
  if (group.frameId || !group.paper) return [];
  try {
    parseSizeId(group.sizeId); // sanity: a malformed group resolves to nothing
  } catch {
    return [];
  }
  return framedUpsellProducts(data, buildCatalog(data), group.paper, colour);
}

export interface FrameExplainer {
  border: BorderStyle;
  /** Short border-style label, e.g. "Double Matt Border" / "Print-to-Edge". */
  label: string;
  /** Full explainer sentence shown under the preview. */
  detail: string;
}

/** Human explainer for a framed option, derived from its border + sizes. */
export function frameExplainer(data: PricingData, option: FramedProduct): FrameExplainer {
  const label = data.productLines.framing.borderStyles[option.border];
  const printSizeLabel = sizeLabel(option.printSize, option.printInches);
  const detail =
    isMattedBorder(option.border)
      ? `Your ${printSizeLabel} print is matted inside a ${option.frameSize}" ${option.colourLabel.toLowerCase()} timber frame.`
      : `Your print fills the ${option.frameSize}" ${option.colourLabel.toLowerCase()} timber frame, edge to edge.`;
  return { border: option.border, label, detail };
}

/**
 * The SAME-SIZE framed product for a loose (size, paper), or null — the
 * eligibility gate for the post-Add frame nudge (owner 2026-08-15, upsell
 * plan #1). A 4×6 has no frame config, so a 4×6 customer is never asked.
 * Prefers the print's own paper via framedUpsellProducts' stand-in logic.
 */
export function sameSizeFrameOption(
  data: PricingData,
  sizeId: string,
  paper: CartGroup['paper'],
): FramedProduct | null {
  if (!paper) return null;
  let size: string;
  try {
    size = parseSizeId(sizeId).size;
  } catch {
    return null;
  }
  const options = frameOptionsForGroup(data, { sizeId, paper, frameId: undefined });
  return options.find((o) => o.printSize === size) ?? null;
}
